/**
 * Login Component
 * Supports Microsoft Entra ID (Azure AD)
 * Feature: User Management & Entra ID Integration
 */

import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import './Login.css';

const Login = ({ onSuccess, onError }) => {
  const { loginWithEntra, authError, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorLog, setErrorLog] = useState(null);
  const [showErrorLog, setShowErrorLog] = useState(false);

  const handleEntraLogin = async () => {
    setError('');
    setLoading(true);
    try {
      // Default landing page after login is the Learning Day Agenda dashboard.
      const redirectPath = sessionStorage.getItem('auth_redirect') || '/';
      await loginWithEntra(redirectPath);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Entra ID login error:', err);
      setError(err.message || 'Microsoft sign-in failed. Please try again.');
      if (onError) onError(err);
      setLoading(false);
    }
  };

  const copyErrorLog = () => {
    if (!errorLog) return;

    const logText = JSON.stringify(errorLog, null, 2);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(logText).then(() => {
        alert('Error log copied to clipboard!');
      }).catch(err => {
        console.error('Failed to copy to clipboard:', err);
      });
    }
  };

  const displayError = error || authError;

  return (
    <div className="login-screen">
      {/* Decorative background (purely visual) */}
      <div className="bg" aria-hidden="true">
        <div className="bg-glow"></div>
        <svg className="bg-wave bg-wave--left" viewBox="0 0 360 360" preserveAspectRatio="none" focusable="false">
          <path d="M-40 345 C 64 280, 98 159, 154 17 C 171 -26, 201 -30, 227 -42" />
          <path d="M-30 356 C 73 293, 108 171, 166 29 C 185 -15, 213 -19, 239 -32" />
          <path d="M-20 367 C 84 305, 119 183, 179 42 C 199 -3, 226 -8, 251 -21" />
          <path d="M-10 378 C 95 317, 131 196, 192 56 C 213 10, 239 4, 264 -9" />
          <path d="M0 389 C 106 330, 143 209, 205 70 C 227 24, 252 17, 277 3" />
          <path d="M10 400 C 118 343, 155 222, 218 84 C 241 38, 265 30, 290 15" />
        </svg>
        <svg className="bg-wave bg-wave--right" viewBox="0 0 460 440" preserveAspectRatio="none" focusable="false">
          <path d="M500 -20 C 395 90, 318 204, 249 340 C 202 432, 133 457, -20 486" />
          <path d="M510 -8 C 405 102, 328 216, 260 352 C 213 444, 144 469, -10 498" />
          <path d="M520 4 C 415 114, 338 228, 271 364 C 224 456, 155 481, 0 510" />
          <path d="M530 16 C 425 126, 348 240, 282 376 C 235 468, 166 493, 10 522" />
          <path d="M540 28 C 435 138, 358 252, 293 388 C 246 480, 177 505, 20 534" />
          <path d="M550 40 C 445 150, 368 264, 304 400 C 257 492, 188 517, 30 546" />
          <path d="M560 52 C 455 162, 378 276, 315 412 C 268 504, 199 529, 40 558" />
        </svg>
        <div className="particle-field">
          {Array.from({ length: 16 }, (_, index) => <span key={index}></span>)}
        </div>
        <span className="bg-dot bg-dot--one"></span>
        <span className="bg-dot bg-dot--two"></span>
        <span className="bg-dot bg-dot--three"></span>
        <span className="bg-dot bg-dot--four"></span>
        <div className="bg-vignette"></div>
      </div>

      <section className="hero" aria-labelledby="app-title">
        <div className="logo">
          <svg className="logo-cap" viewBox="0 0 220 230" role="img" aria-labelledby="cap-title cap-desc">
            <title id="cap-title">Learning Day graduation cap</title>
            <desc id="cap-desc">A turquoise line-art graduation cap with a flowing tassel.</desc>
            <g className="cap-line" fill="none" stroke="var(--cyan)" strokeWidth="4.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 48 C42 34 96 25 142 15 L196 52 L116 91 L18 55 C13 53 13 50 18 48Z" />
              <path d="M18 54 L32 75 M142 15 C151 42 168 67 196 52" />
              <path d="M39 71 C47 50 65 43 87 47 C111 51 133 66 151 85 C159 93 169 98 176 91 C183 84 174 76 166 83 C146 101 132 116 127 137" />
              <path d="M28 92 C43 78 75 91 106 110 C125 121 139 133 134 144 C130 152 119 150 110 147 C75 137 43 120 29 105 C24 100 24 96 28 92Z" />
              <path d="M143 77 C154 69 165 70 172 79 C180 89 173 100 163 98 C154 96 154 88 161 83" />
              <path d="M176 55 C194 49 205 59 197 75 C190 91 173 105 161 118 C148 133 144 155 149 174 C154 192 174 194 178 181 C181 170 168 163 158 170 C146 178 144 194 149 213" />
            </g>
          </svg>

          <h1 id="app-title" className="logo-text">Learning<br />Day</h1>
        </div>

        <div className="intro-copy">
          <p className="tagline">LEARN <span>•</span> CONNECT <span>•</span> GROW <span>•</span> TOGETHER</p>
          <p className="intro-subtitle">Skills for today. Ideas for tomorrow.</p>
        </div>

        {displayError && (
          <div className="login-error" role="alert">
            <div>{displayError}</div>
            {errorLog && (
              <button
                type="button"
                onClick={() => setShowErrorLog(true)}
                className="error-details-btn"
              >
                📋 Show Detailed Error Log
              </button>
            )}
          </div>
        )}

        <div className="sign-in">
          <button
            type="button"
            onClick={handleEntraLogin}
            disabled={loading || authLoading}
            className="ms-btn"
            aria-label="Sign in with Microsoft"
          >
            <svg className="ms-btn__logo" width="24" height="24" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
              <rect x="1" y="1" width="9" height="9" fill="#F35325" />
              <rect x="11" y="1" width="9" height="9" fill="#81BC06" />
              <rect x="1" y="11" width="9" height="9" fill="#05A6F0" />
              <rect x="11" y="11" width="9" height="9" fill="#FFBA08" />
            </svg>
            <span className="ms-btn__label">{loading ? 'Connecting...' : 'Sign in with Microsoft'}</span>
            <svg className="ms-btn__arrow" width="20" height="20" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                 aria-hidden="true" focusable="false">
              <path d="M4 12h15M13 6l6 6-6 6" />
            </svg>
          </button>
        </div>

        <div className="learning-pillars" aria-label="Learning Day values">
          <div className="pillar">
            <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
              <path d="M20 9C15 5 9 5 4 7v24c5-2 11-2 16 2m0-24c5-4 11-4 16-2v24c-5-2-11-2-16 2m0-24v24" />
            </svg>
            <span>Explore<br />Learning Paths</span>
          </div>
          <div className="pillar">
            <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
              <circle cx="20" cy="12" r="6" />
              <path d="M9 33v-4c0-5 5-8 11-8s11 3 11 8v4H9Zm-2-18a5 5 0 1 0 0 10m26-10a5 5 0 1 1 0 10M5 33v-3c0-3 2-5 5-6m25 9v-3c0-3-2-5-5-6" />
            </svg>
            <span>Build<br />Your Skills</span>
          </div>
          <div className="pillar">
            <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
              <path d="M20 4a13 13 0 0 0-8 23c2 2 3 4 3 6h10c0-2 1-4 3-6a13 13 0 0 0-8-23Z" />
              <path d="M15 36h10m-9-7h8m-4-19a7 7 0 0 1 7 7" />
            </svg>
            <span>Share<br />Knowledge</span>
          </div>
        </div>

        <footer className="brand-footer">
          <div className="footer-motto"><span></span>A STRONGER TOMORROW, TOGETHER<span></span></div>
          <div className="siemens-wordmark" aria-label="Siemens">SIEMENS</div>
        </footer>
      </section>

      {/* Error Log Modal */}
      {showErrorLog && errorLog && (
        <div className="modal-overlay">
          <div className="modal-content error-modal">
            <div className="modal-header error-header">
              <h3>❌ Login Error - Detailed Diagnostic Log</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowErrorLog(false)}
                aria-label="Close error log modal"
              >
                ✖
              </button>
            </div>
            <div className="modal-body">
              <div className="error-summary">
                <p><strong>Timestamp:</strong> {new Date(errorLog.timestamp).toLocaleString()}</p>
                <p><strong>Username:</strong> {errorLog.username}</p>
                <p><strong>Request URL:</strong> {errorLog.requestUrl}</p>
                <p><strong>Request Method:</strong> {errorLog.requestMethod}</p>
                {errorLog.statusCode && <p><strong>HTTP Status:</strong> {errorLog.statusCode}</p>}
                {errorLog.responseStatus && <p><strong>Response Status:</strong> {errorLog.responseStatus} {errorLog.responseStatusText}</p>}
                {errorLog.networkError && (
                  <p style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                    ⚠️ Network Error Detected - Backend may be offline
                  </p>
                )}
                <p><strong>Browser Online:</strong> {errorLog.browserInfo?.online ? '✅ Yes' : '❌ No'}</p>
              </div>

              <div className="error-details">
                <h4>Error Message:</h4>
                <pre className="error-code">{errorLog.errorMessage}</pre>

                {errorLog.serverResponse && (
                  <>
                    <h4>Server Response:</h4>
                    <pre className="error-code">{JSON.stringify(errorLog.serverResponse, null, 2)}</pre>
                  </>
                )}

                <h4>Full Error Details:</h4>
                <pre className="error-code">{errorLog.fullError}</pre>

                {errorLog.errorStack && (
                  <>
                    <h4>Stack Trace:</h4>
                    <pre className="error-code">{errorLog.errorStack}</pre>
                  </>
                )}

                <h4>Browser Information:</h4>
                <pre className="error-code">{JSON.stringify(errorLog.browserInfo, null, 2)}</pre>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-modal-cancel" onClick={() => setShowErrorLog(false)}>
                Close
              </button>
              <button className="btn-modal-proceed" onClick={copyErrorLog}>
                📋 Copy Error Log to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
